// Resolves the ids that runs, tasks and approvals carry (agentId, projectId,
// taskId, roleId, executorId) into names, so no page has to show a raw UUID.
// Each list is fetched once per workspace and shared through the query cache;
// an id that isn't found (or a list that failed to load) falls back to its
// short id.

import { useQuery } from '@tanstack/react-query'
import {
  CONNECTION_KIND_LABEL,
  agentsApi,
  connectionsApi,
  executorsApi,
  type ConnectionKind,
  projectsApi,
  rolesApi,
  tasksApi,
  workspaceApi,
} from '../lib/api-client'
import { useWorkspaceId } from '../lib/auth'
import { shortId } from '../lib/format'

const STALE_MS = 60_000

export function useLookups() {
  const workspaceId = useWorkspaceId()
  const enabled = workspaceId !== ''

  const agents = useQuery({
    queryKey: ['agents', workspaceId],
    queryFn: () => agentsApi.list({ workspaceId }),
    enabled,
    staleTime: STALE_MS,
  })
  const projects = useQuery({
    queryKey: ['projects', workspaceId],
    queryFn: () => projectsApi.list({ workspaceId }),
    enabled,
    staleTime: STALE_MS,
  })
  const tasks = useQuery({
    queryKey: ['tasks', workspaceId, {}],
    queryFn: () => tasksApi.list({ workspaceId }),
    enabled,
    staleTime: STALE_MS,
  })
  const roles = useQuery({
    queryKey: ['roles', workspaceId],
    queryFn: () => rolesApi.list({ workspaceId }),
    enabled,
    staleTime: STALE_MS,
  })
  const executors = useQuery({
    queryKey: ['executors', workspaceId],
    queryFn: () => executorsApi.list({ workspaceId }),
    enabled,
    staleTime: STALE_MS,
  })

  const connections = useQuery({
    queryKey: ['connections', workspaceId],
    queryFn: () => connectionsApi.list(workspaceId),
    enabled,
    staleTime: STALE_MS,
  })

  const workspace = useQuery({
    queryKey: ['workspace', workspaceId],
    queryFn: () => workspaceApi.get(workspaceId),
    enabled,
    staleTime: STALE_MS,
  })

  function nameOf<T extends { id: string }>(
    items: T[] | undefined,
    id: string | null | undefined,
    key: (item: T) => string
  ) {
    if (!id) return '—'
    const found = items?.find((item) => item.id === id)
    return found ? key(found) : shortId(id)
  }

  return {
    isLoading: {
      agents: agents.isLoading,
      projects: projects.isLoading,
      roles: roles.isLoading,
      executors: executors.isLoading,
      connections: connections.isLoading,
    },
    agents: agents.data?.items ?? [],
    projects: projects.data?.items ?? [],
    tasks: tasks.data?.items ?? [],
    roles: roles.data?.items ?? [],
    executors: executors.data?.items ?? [],
    /** Named executor logins agents pick from (several per kind). */
    connections: connections.data?.items ?? [],
    /** The workspace's "Run costs" setting, for `formatRunCost`. */
    runCostDisplay: workspace.data?.runCostDisplay ?? 'estimate',
    agentName: (id: string | null | undefined) =>
      nameOf(agents.data?.items, id, (a) => a.name),
    projectName: (id: string | null | undefined) =>
      nameOf(projects.data?.items, id, (p) => p.name),
    taskTitle: (id: string | null | undefined) =>
      nameOf(tasks.data?.items, id, (t) => t.title),
    roleName: (id: string | null | undefined) =>
      nameOf(roles.data?.items, id, (r) => r.name),
    /** Executors are referred to by type (agents, runs) or by profile id. */
    executorName: (id: string | null | undefined) =>
      executors.data?.items.find((e) => e.type === id)?.name ??
      nameOf(executors.data?.items, id, (e) => e.name),
    /** What an agent runs with: its connection's name, else its executor kind. */
    agentConnection: (agent: {
      executorId?: string | null
      executorCredentialId?: string | null
    }) => {
      const connection = connections.data?.items.find(
        (c) => c.id === agent.executorCredentialId
      )
      if (connection) return connection.name
      return (
        CONNECTION_KIND_LABEL[agent.executorId as ConnectionKind] ??
        executors.data?.items.find((e) => e.type === agent.executorId)?.name ??
        agent.executorId ??
        '—'
      )
    },
    task: (id: string | null | undefined) =>
      tasks.data?.items.find((t) => t.id === id),
    /** Role name of an agent (for its avatar); undefined when unknown. */
    agentRoleName: (agentId: string | null | undefined) => {
      const agent = agents.data?.items.find((a) => a.id === agentId)
      return agent
        ? roles.data?.items.find((r) => r.id === agent.roleId)?.name
        : undefined
    },
  }
}
